/**
 * What the author put in one binding slot (CT-5725).
 *
 * Since the Data step became a plain record set, the slot *is* the
 * configuration: it carries the column the author chose and, on a measure
 * slot, the operation to run over it. So an assignment is an object —
 * `{ field, aggregate }` — where `field` is the column's **field uuid**, the
 * same id every other selection in the builder stores, and the payload turns
 * it into the dataset's `fieldKey`.
 *
 * `COUNT_ROWS` is the one item with no column: it counts the rows of the
 * route, and the schema rejects it with a `field`.
 *
 * A slot the contract declares as an array holds a list of these, in the
 * order the author built it.
 */

import { AGGREGATE_WITHOUT_FIELD } from '../constants/widgetContract.js';

const EMPTY = Object.freeze([]);

/** A stored value → one item. A bare string is the pre-CT-5725 shape. */
export const toItem = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return { field: value };
  return value;
};

/** A slot's value → its items, however it is stored. */
export const toItems = (value) => {
  if (!value) return EMPTY;
  return (Array.isArray(value) ? value : [value]).map(toItem).filter(Boolean);
};

/** Counting rows reads no column, so an item with it is complete without one. */
export const countsRows = (item) => item?.aggregate === AGGREGATE_WITHOUT_FIELD;

/**
 * Is this item ready to travel?
 *
 * A measure slot needs its operation, and every operation but `COUNT_ROWS`
 * needs a column. A dimension or a column slot needs the column alone.
 */
export const isComplete = (item, { needsAggregate = false } = {}) => {
  if (!item) return false;
  if (needsAggregate && !item.aggregate) return false;
  if (countsRows(item)) return true;
  return Boolean(item.field);
};

/** The items of a slot that are ready, in order. */
export const completeItems = (value, options) =>
  toItems(value).filter((item) => isComplete(item, options));

/** Has the author answered this slot at all? */
export const isAnswered = (value, options) => completeItems(value, options).length > 0;

/**
 * Write one item back into a slot's value, keeping its shape: an array slot
 * stays an array, a single one stays a single item, and an emptied item
 * leaves the slot unanswered.
 */
export const withItem = (value, index, item) => {
  const items = toItems(value);
  const next = [...items];
  if (item === null) next.splice(index, 1);
  else next[index] = item;
  return next;
};

export default toItems;
